/**
 * Shared scheduling primitives used by every "schedule recurring runs" surface
 * (exploratory tests, test suites). Backed by the generic automations framework
 * — see `useScheduleRules`.
 */

/**
 * Quick one-tap schedules offered in the schedule modals alongside the agent
 * textbox. Pure data so it can be unit-tested; `description` is the display
 * string stored on the rule (the source of truth shown in tables/modals).
 */
export const SCHEDULE_PRESETS: ReadonlyArray<{
  label: string;
  cron: string;
  description: string;
}> = [
  { label: 'Daily', cron: '0 9 * * *', description: 'Every day at 9:00 AM' },
  { label: 'Weekdays', cron: '0 9 * * 1-5', description: 'Every weekday at 9:00 AM' },
  { label: 'Weekly', cron: '0 9 * * 1', description: 'Every Monday at 9:00 AM' },
];

/** The viewer's IANA timezone, falling back to UTC when unavailable. */
export const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** Format an ISO next-run timestamp for display, or an em dash when absent. */
export const formatNextRun = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/**
 * A schedule's current cadence in words: the preset's wording when the cron is
 * one of the quick schedules, otherwise the raw cron pattern; always with its
 * timezone. Never the schedule's name.
 */
export const describeCadence = (cron: string | null, timezone: string | null): string => {
  const base = cron
    ? (SCHEDULE_PRESETS.find((preset) => preset.cron === cron)?.description ?? `Cron ${cron}`)
    : 'No cadence set';
  return timezone ? `${base} (${timezone})` : base;
};
